import flagsmith from '@flagsmith/flagsmith';
import {IFlagsmithFeature, IState} from '@flagsmith/flagsmith/types';
import {Card, Label, Search, Separator, Switch, useLocalStorage} from 'platform/components';
import {Grid, Hide, HStack, Scroll, Show, Space, Text, VStack} from 'platform/foundation';

import {useMemo, useState} from 'react';

import {filter, isNotNil, map, pipe, toPairs} from 'ramda';
import {isFalsy, isNilOrEmpty, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {browserStorageKey} from '@dms/config';

import {getFixedTranslation} from '../utils/getFixedTranslation';
import {triggerFeatureFlagMockChange} from './triggerFeatureFlagMockChange';

type UpdatedFlags = Record<string, {enabled: boolean; originalState?: boolean}> | null;

const getFlagsFromState =
  (searchString: string | null) =>
  (state: IState): [string, IFlagsmithFeature][] =>
    Object.entries(state?.flags ?? {}).filter(
      ([key]) => !searchString || key.toLowerCase().includes(searchString.toLowerCase())
    ) ?? [];

const sortFlagsAlphabetically = (flags: [string, IFlagsmithFeature][]) =>
  flags.sort(([a], [b]) => a.localeCompare(b));

export function FeatureFlagsMock() {
  const [updatedFlags, setUpdatedFlags] = useLocalStorage<UpdatedFlags>(
    browserStorageKey.MOCK_FLAGS_KEY,
    null
  );
  const isMocking = isNotNil(updatedFlags);

  const [searchString, setSearchString] = useState<string | null>(null);

  const flags = useMemo(() => {
    const flagsmithState = flagsmith.getState();

    return pipe(
      getFlagsFromState(searchString),
      filter(([key]: [string, IFlagsmithFeature]) => !updatedFlags?.hasOwnProperty(key)),
      sortFlagsAlphabetically,
      map(([key, value]) => ({key, ...value}))
    )(flagsmithState);
  }, [searchString, updatedFlags]);

  const toggleFlag = (key: string, enabled: boolean, originalState?: boolean) => {
    setUpdatedFlags((prev) => ({
      ...prev,
      [key]: {enabled, originalState: prev?.[key]?.originalState ?? originalState},
    }));
    // Trigger re-render for same-tab changes
    triggerFeatureFlagMockChange();
  };

  const handleToggleIsMocking = (val: boolean) => {
    if (val) {
      setUpdatedFlags({});
    } else {
      setUpdatedFlags(null);
      localStorage.removeItem(browserStorageKey.MOCK_FLAGS_KEY);
    }
    // Trigger re-render for same-tab changes
    triggerFeatureFlagMockChange();
  };

  return (
    <Card
      control={{
        type: 'switch',
        value: isMocking,
        onChange: handleToggleIsMocking,
      }}
      title={getFixedTranslation('page.settings.labels.featureFlagsMock')}
      isExpanded={isMocking}
      variant="inlineGrey"
      flags={[
        isMocking
          ? {
              label: getFixedTranslation('general.labels.on'),
              colorScheme: 'orange',
              isSubtle: true,
            }
          : {
              label: getFixedTranslation('general.labels.off'),
              colorScheme: 'neutral',
              isSubtle: true,
            },
      ]}
    >
      <VStack spacing={2}>
        <Search value={searchString} onChange={setSearchString} />

        <Hide when={isNilOrEmpty(updatedFlags)}>
          <Text size="small" color="link">
            {getFixedTranslation('page.settings.labels.updatedFlags')}
          </Text>
          <Separator spacing={0} />

          <VStack spacing={1}>
            {toPairs(updatedFlags ?? {}).map(([key, {enabled, originalState}]) => (
              <HStack key={key} spacing={2} align="center">
                <Switch value={enabled} label={key} onChange={(val) => toggleFlag(key, val)} />
                <Show when={isNotNilOrEmpty(originalState)}>
                  <HStack align="baseline">
                    <Label>{`| ${getFixedTranslation('page.settings.labels.originalState')}`}</Label>
                    <Text size="xSmall" color={originalState ? 'success' : 'danger'}>
                      {`: ${originalState}`}
                    </Text>
                  </HStack>
                </Show>
              </HStack>
            ))}
          </VStack>
        </Hide>

        <Grid columns={2} align="flex-end">
          <Text size="small" color="success">
            {getFixedTranslation('page.settings.labels.enabledFlags')}
          </Text>
          <Text size="small" color="danger">
            {getFixedTranslation('page.settings.labels.disabledFlags')}
          </Text>
        </Grid>
        <Separator spacing={0} />
      </VStack>
      <Scroll auto maxHeight={100}>
        <Space vertical={4} />
        <Grid columns={2}>
          <VStack spacing={1}>
            {flags
              .filter(({enabled}) => isTrue(enabled))
              .map(({key, enabled}) => (
                <HStack key={key}>
                  <Switch
                    value={enabled}
                    label={key}
                    onChange={(val) => toggleFlag(key, val, true)}
                  />
                </HStack>
              ))}
          </VStack>
          <VStack spacing={1}>
            {flags
              .filter(({enabled}) => isFalsy(enabled))
              .map(({key, enabled}) => (
                <HStack key={key}>
                  <Switch
                    value={enabled}
                    label={key}
                    onChange={(val) => toggleFlag(key, val, false)}
                  />
                </HStack>
              ))}
          </VStack>
        </Grid>
      </Scroll>
    </Card>
  );
}
